import React from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from 'react-bootstrap';
import ItemDetail from 'components/parts/item/itemDetail';
import StatusInfoPopup from 'components/adm/account/statusInfoPopup';
import Message from 'components/parts/message';
import { getAccount, deleteAccount, toggleAccountEnableDisable } from 'actions/accountAction';
import { setBreadcrumb } from 'actions/breadcrumbAction';
import { clearMessage, setMessage} from 'actions/messageAction';
import { validType } from 'const';
import { convertTimestampToDate, consoleLogger } from 'helpers';
import { lang } from 'lang';

/**
 * 管理者アカウント詳細画面
 */
const AdmAccountDetail = (props) => {
  const dispatch = useDispatch();
  const { id } = props.match.params;
  const { msg, error } = lang;
  const [ buttonStatus, setButtonStatus ] = React.useState({
    delete: false,   // 削除ボタンの活性化状態
    enabled: false,  // 有効化・無効化ボタンの活性化状態
    reload: false    // リロードボタンの活性化状態
  });
  // グローバルステートから対象の管理者アカウントを取り出す
  const accountState = useSelector(
    (state) => state.account.items.filter((item) => item.sub === id)
  );
  // リクエストキャンセルトークンを取得
  const cancelToken = axios.CancelToken.source();

  React.useEffect(() => {
    // 管理者アカウント情報がステートにない場合
    if (accountState.length === 0) {
      getData(id, cancelToken);
    } else {
      dispatch(clearMessage());
      dispatch(setBreadcrumb(accountState[0].name, accountState[0].sub));
    }
    return () => {
      // unmounted component時にコールされる
      cancelToken.cancel('unmounted component.');
    };
  }, []);

  // 指定したid(=sub)と一致する管理者アカウント情報を取得する
  const getData = async (id, cancelToken) => {
    try {
      // メッセージクリア
      dispatch(clearMessage());
      await dispatch(
        await getAccount(id, cancelToken)
      ).then((result) => {
        if (result.status === 200) {
          dispatch(setBreadcrumb(result.data.name, result.data.sub));
        } else if (result.status === 404) {
          dispatch(setMessage('info', msg.account.not_exist));
        } else {
          dispatch(setMessage('danger', error.account.get + `\n${result.errcode}`));
        }
      });
    } catch (e) {
      consoleLogger('Fail get account detail.', e);
    }
  };

  // 管理者アカウント情報がステートにない場合
  if (accountState.length === 0) {
    return <div className="col-10 pt-3 pb-3"><Message/></div>;
  } else {      
    /**
     * 削除ボタンが押下されたときに実行するメソッド
    */
    const clickDeleteButton = async sub => {
      dispatch(clearMessage());
      // 削除・リロードボタンを非活性化
      setButtonStatus({...buttonStatus, delete: true, reload: true});
      await dispatch(
        await deleteAccount(sub)
      ).then((result) => {
        // Success
        if (result.status === 200) {
          props.history.replace({
            pathname: '/adm/accounts',
            state: { 
              keepMessage: true,  // 一覧画面表示時にメッセージを残す
              message: {
                type: 'success',
                msg: msg.account.remove
              }
            }
          });
        // Error
        } else {
          dispatch(setMessage('danger', error.account.remove + `\n${result.errcode}`));
        }
      });
      // 削除・リロードボタンを活性化
      setButtonStatus({...buttonStatus, delete: false, reload: false});
    };

    /**
    * 有効/無効ボタンが押下されたときに実行するメソッド
    */
    const toggleButton = async (sub, enabled) => {
      dispatch(clearMessage());
      // 削除・有効/無効・リロードを非活性化
      setButtonStatus({delete: true, enabled: true, reload: true});
      await dispatch(
        await toggleAccountEnableDisable(sub, enabled)
      ).then(async (result) => {
        // Success
        if (result.status === 200) {
          if (enabled) {
            dispatch(setMessage('success', msg.account.validate));
          } else {
            dispatch(setMessage('success', msg.account.invalidate));
          }
        // Not Found
        } else if (result.status === 404) {
          dispatch(setMessage('danger', msg.account.not_exist));
        // Error
        } else {
          if (enabled) {
            dispatch(setMessage('danger', error.account.validate + `\n${result.errcode}`));
          } else {
            dispatch(setMessage('danger', error.account.invalidate + `\n${result.errcode}`));
          }
        }
      });
      // 削除・有効/無効・リロードボタンを活性化
      setButtonStatus({delete: false, enabled: false, reload: false});
    };

    return (
      <>
        <div
          className="col-10 pt-3 pb-3"
        >
          <Div>
            {!accountState[0].enabled && 
            <DeleteButton 
              variant="danger"
              disabled={buttonStatus.delete} 
              onClick={() => clickDeleteButton(id)}
            >
              Remove
            </DeleteButton>}
            <OpenButton
              disabled={buttonStatus.enabled} 
              onClick={() => toggleButton(id, !accountState[0].enabled)}
            >
              {accountState[0].enabled ? 'Invalidate' : 'Validate'}
            </OpenButton>
          </Div>
          <ItemDetail 
            title='Detail information' 
            labels={{
              name: 'Name',
              preferred_username: 'Organization',
              email: 'E-mail',
              phone_number: 'Phone',
              sub: 'sub ID',
              enabled: 'Status',
              user_status: <>Verification<StatusInfoPopup /></>,
              created_at: 'Created date',
              updated_at: 'Last update',
            }} 
            items={{
              ...accountState[0],
              enabled: validType[accountState[0].enabled],
              created_at: convertTimestampToDate(accountState[0].created_at),
              updated_at: convertTimestampToDate(accountState[0].updated_at),
            }} 
            operation={{ 
              edit: { path: `/adm/account/edit/${accountState[0].sub}` },
              reload: { 
                reloadFunc: async () => { 
                  setButtonStatus({delete: true, enabled: true,reload: true});
                  await getData(id, cancelToken); 
                  setButtonStatus({delete: false, enabled: false,reload: false});
                },
                disabled: buttonStatus.reload
              },
            }}/>
        </div>
      </>
    );
  }
};
AdmAccountDetail.propTypes = {
  match: PropTypes.object
};
AdmAccountDetail.defaultProps = {
  match: {
    params: {
      id: '',
    }
  }
};
const Div = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
`;
const DeleteButton = styled(Button)`
  margin-right: 10px;
`;
const OpenButton = styled(Button)`
  margin-right: 10px;
`;
export default AdmAccountDetail;
